/**
 * 📊 PROSPECTS DASHBOARD - Tableau de bord Bon -10%
 * Suivi des performances : double opt-in, conversions, codes utilisés
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Mail, Users, TrendingUp, Clock, CheckCircle, XCircle,
  RefreshCw, Send, Eye, Gift, BarChart3, Target,
  MousePointer, UserCheck, Zap, Percent, ShoppingBag,
  MapPin, Phone, Calendar, Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../services/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Carte stat principale
const StatCard = ({ icon: Icon, label, value, subValue, color, percentage }) => {
  const colorMap = {
    green: 'from-green-500/20 to-emerald-500/20 border-green-500/30 text-green-400',
    cyan: 'from-cyan-500/20 to-blue-500/20 border-cyan-500/30 text-cyan-400',
    yellow: 'from-yellow-500/20 to-orange-500/20 border-yellow-500/30 text-yellow-400',
    orange: 'from-orange-500/20 to-red-500/20 border-orange-500/30 text-orange-400',
    purple: 'from-purple-500/20 to-pink-500/20 border-purple-500/30 text-purple-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      className={`bg-gradient-to-br ${colorMap[color]} rounded-2xl p-5 border backdrop-blur-xl`}
    >
      <div className="flex items-start justify-between">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorMap[color]} flex items-center justify-center border`}>
          <Icon className="w-6 h-6" />
        </div>
        {percentage !== undefined && (
          <span className={`text-xs px-2 py-1 rounded-full ${percentage > 50 ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
            {percentage}%
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-3xl font-black text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
        <p className="text-sm text-gray-400">{label}</p>
        {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
      </div>
    </motion.div>
  );
};

// Funnel de conversion double opt-in
const DoubleOptinFunnel = ({ stats, prospects }) => {
  const unconfirmed = stats?.unconfirmed || 0;
  const confirmed = stats?.confirmed || 0;
  const converted = prospects?.filter(p => p.promoUsed)?.length || 0;
  const total = stats?.total || 0;

  const steps = [
    { label: 'Inscrits', value: total, percent: 100, color: 'cyan', icon: Users },
    { label: 'En attente confirmation', value: unconfirmed, percent: total > 0 ? Math.round((unconfirmed / total) * 100) : 0, color: 'yellow', icon: Clock },
    { label: 'Email confirmé', value: confirmed, percent: total > 0 ? Math.round((confirmed / total) * 100) : 0, color: 'green', icon: CheckCircle },
    { label: 'Code utilisé', value: converted, percent: confirmed > 0 ? Math.round((converted / confirmed) * 100) : 0, color: 'orange', icon: ShoppingBag },
  ];

  return (
    <div className="bg-[#12121a] rounded-2xl p-6 border border-green-500/20">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Target className="w-5 h-5 text-green-400" />
        Funnel Double Opt-in → Conversion
      </h3>
      
      <div className="space-y-3">
        {steps.map((step, index) => (
          <motion.div
            key={step.label}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-8 h-8 rounded-lg bg-${step.color}-500/20 flex items-center justify-center`}>
                <step.icon className={`w-4 h-4 text-${step.color}-400`} />
              </div>
              <div className="flex-1 flex justify-between items-center">
                <span className="text-gray-400 text-sm">{step.label}</span>
                <span className="text-white font-bold">{step.value}</span>
              </div>
            </div>
            <div className="h-3 bg-gray-800 rounded-full overflow-hidden ml-11">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${step.percent}%` }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="h-full rounded-full"
                style={{ 
                  background: step.color === 'cyan' ? 'linear-gradient(90deg, #06b6d4, #3b82f6)' :
                             step.color === 'yellow' ? 'linear-gradient(90deg, #eab308, #f97316)' :
                             step.color === 'green' ? 'linear-gradient(90deg, #22c55e, #10b981)' :
                             'linear-gradient(90deg, #f97316, #ef4444)'
                }}
              />
            </div>
            <div className="text-right mr-2 mt-1">
              <span className={`text-xs text-${step.color}-400`}>{step.percent}%</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 mt-6 pt-4 border-t border-gray-800">
        <div className="text-center p-3 bg-green-500/10 rounded-xl">
          <p className="text-2xl font-black text-green-400">{stats?.confirmationRate || 0}%</p>
          <p className="text-xs text-gray-400">Taux confirmation</p>
        </div>
        <div className="text-center p-3 bg-orange-500/10 rounded-xl">
          <p className="text-2xl font-black text-orange-400">
            {confirmed > 0 ? Math.round((converted / confirmed) * 100) : 0}%
          </p>
          <p className="text-xs text-gray-400">Taux utilisation code</p>
        </div>
      </div>
    </div>
  );
};

// Graphique des inscriptions par jour
const SignupChart = ({ prospects }) => {
  // Grouper par jour (7 derniers jours)
  const last7Days = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    const count = prospects.filter(p => p.createdAt?.startsWith(dateStr)).length;
    last7Days.push({
      date: date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' }),
      count
    });
  }

  const maxCount = Math.max(...last7Days.map(d => d.count), 1);

  return (
    <div className="bg-[#12121a] rounded-2xl p-6 border border-cyan-500/20">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-cyan-400" />
        Inscriptions (7 derniers jours)
      </h3>
      
      <div className="flex items-end justify-between gap-2 h-32">
        {last7Days.map((day, index) => (
          <motion.div
            key={day.date}
            initial={{ height: 0 }}
            animate={{ height: `${(day.count / maxCount) * 100}%` }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="flex-1 flex flex-col items-center"
          >
            <div 
              className="w-full bg-gradient-to-t from-cyan-500 to-blue-500 rounded-t-lg min-h-[4px]"
              style={{ height: `${Math.max((day.count / maxCount) * 100, 5)}%` }}
            />
            <span className="text-xs text-gray-500 mt-2">{day.date}</span>
            <span className="text-xs text-cyan-400 font-bold">{day.count}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// Liste des codes promo actifs
const ActivePromoCodes = ({ prospects }) => {
  const confirmedProspects = prospects
    .filter(p => p.emailConfirmed && !p.promoUsed && p.codePromo)
    .slice(0, 8);

  return (
    <div className="bg-[#12121a] rounded-2xl p-6 border border-yellow-500/20">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Gift className="w-5 h-5 text-yellow-400" />
        Codes Promo Actifs ({confirmedProspects.length})
      </h3>
      
      {confirmedProspects.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-4">Aucun code actif</p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {confirmedProspects.map((prospect) => (
            <motion.div
              key={prospect.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-3 bg-yellow-500/10 rounded-xl border border-yellow-500/20"
            >
              <div className="flex items-center justify-between">
                <span className="text-yellow-400 font-mono font-bold text-sm">
                  🎫 {prospect.codePromo}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1 truncate">{prospect.prenom}</p>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Composant principal
export default function ProspectsDashboard() {
  const [stats, setStats] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProspects, setSelectedProspects] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [statsResponse, prospectsResponse] = await Promise.all([
        fetch(`${API_URL}/api/prospects/stats`).then(r => r.json()),
        fetch(`${API_URL}/api/prospects`).then(r => r.json())
      ]);
      
      setStats(statsResponse);
      setProspects(prospectsResponse.prospects || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Envoyer email aux sélectionnés
  const sendEmailToSelected = async (emailType) => {
    if (selectedProspects.length === 0) {
      toast.error('Sélectionnez des prospects');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/prospects/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospectIds: selectedProspects, emailType })
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(`📧 ${data.sent} emails envoyés !`);
        setSelectedProspects([]);
        loadData();
      }
    } catch (error) {
      toast.error('Erreur d\'envoi');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  const confirmed = stats?.confirmed || 0;
  const unconfirmed = stats?.unconfirmed || 0;
  const total = stats?.total || 0;
  const converted = prospects.filter(p => p.promoUsed).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
            <Percent className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-white">🎁 Dashboard Bon -10%</h2>
            <p className="text-sm text-gray-400">Double opt-in • Codes promo • Conversions</p>
          </div>
        </div>
        <Button
          onClick={loadData}
          variant="outline"
          className="border-green-500/30 text-green-400 hover:bg-green-500/10"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats principales */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          icon={Users}
          label="Total Inscrits"
          value={total}
          subValue="Via /offre-bienvenue"
          color="cyan"
        />
        <StatCard
          icon={Clock}
          label="En attente"
          value={unconfirmed}
          subValue="Double opt-in pending"
          color="yellow"
          percentage={total > 0 ? Math.round((unconfirmed / total) * 100) : 0}
        />
        <StatCard
          icon={CheckCircle}
          label="Confirmés"
          value={confirmed}
          subValue="Email validé"
          color="green"
          percentage={stats?.confirmationRate || 0}
        />
        <StatCard
          icon={Gift}
          label="Codes actifs"
          value={prospects.filter(p => p.emailConfirmed && !p.promoUsed && p.codePromo).length}
          subValue="En attente d'utilisation"
          color="orange"
        />
        <StatCard
          icon={ShoppingBag}
          label="Codes utilisés"
          value={converted}
          subValue="Achat en magasin"
          color="purple"
          percentage={confirmed > 0 ? Math.round((converted / confirmed) * 100) : 0}
        />
      </div>

      {/* Funnel + Graphique */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <DoubleOptinFunnel stats={stats} prospects={prospects} />
        <SignupChart prospects={prospects} />
      </div>

      {/* Codes actifs + Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ActivePromoCodes prospects={prospects} />
        
        {/* Actions rapides */}
        <div className="bg-[#12121a] rounded-2xl p-6 border border-purple-500/20">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-purple-400" />
            Actions Rapides
          </h3>
          
          <div className="space-y-3">
            <Button
              onClick={() => sendEmailToSelected('reminder')}
              disabled={selectedProspects.length === 0}
              className="w-full bg-orange-500/20 text-orange-400 border border-orange-500/30 hover:bg-orange-500/30"
            >
              <Send className="w-4 h-4 mr-2" />
              Rappel code promo ({selectedProspects.length} sélectionnés)
            </Button>
            
            <Button
              onClick={() => sendEmailToSelected('products')}
              disabled={selectedProspects.length === 0}
              className="w-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Email Produits
            </Button>
            
            <Button
              onClick={() => sendEmailToSelected('delivery')}
              disabled={selectedProspects.length === 0}
              className="w-full bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Email Livraison gratuite
            </Button>
          </div>

          <div className="mt-4 p-3 bg-purple-500/10 rounded-xl border border-purple-500/20">
            <p className="text-xs text-gray-400">
              💡 Sélectionnez des prospects dans le tableau ci-dessous pour leur envoyer des emails ciblés.
            </p>
          </div>
        </div>
      </div>

      {/* Tableau des prospects */}
      <div className="bg-[#12121a] rounded-2xl p-6 border border-cyan-500/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-cyan-400" />
            Prospects ({prospects.length})
          </h3>
          {selectedProspects.length > 0 && (
            <span className="text-sm text-cyan-400">
              {selectedProspects.length} sélectionné(s)
            </span>
          )}
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-800">
                <th className="pb-3 pr-4">
                  <input 
                    type="checkbox"
                    checked={selectedProspects.length === prospects.filter(p => p.emailConfirmed).length && prospects.filter(p => p.emailConfirmed).length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedProspects(prospects.filter(p => p.emailConfirmed).map(p => p.id));
                      } else {
                        setSelectedProspects([]);
                      }
                    }}
                    className="accent-cyan-500"
                  />
                </th>
                <th className="pb-3 pr-4">Prénom</th>
                <th className="pb-3 pr-4">Email</th>
                <th className="pb-3 pr-4">Code</th>
                <th className="pb-3 pr-4">Statut</th>
                <th className="pb-3 pr-4">Zone</th>
                <th className="pb-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {prospects.slice(0, 20).map((prospect) => (
                <tr key={prospect.id} className="text-sm hover:bg-white/5">
                  <td className="py-3 pr-4">
                    <input 
                      type="checkbox"
                      checked={selectedProspects.includes(prospect.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProspects([...selectedProspects, prospect.id]);
                        } else {
                          setSelectedProspects(selectedProspects.filter(id => id !== prospect.id));
                        }
                      }}
                      disabled={!prospect.emailConfirmed}
                      className="accent-cyan-500 disabled:opacity-30"
                    />
                  </td>
                  <td className="py-3 pr-4 font-medium text-white">{prospect.prenom}</td>
                  <td className="py-3 pr-4 text-gray-400">{prospect.email}</td>
                  <td className="py-3 pr-4">
                    {prospect.codePromo && prospect.emailConfirmed ? (
                      <span className={`px-2 py-0.5 rounded text-xs font-mono ${
                        prospect.promoUsed 
                          ? 'bg-gray-500/20 text-gray-400 line-through' 
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {prospect.codePromo}
                      </span>
                    ) : (
                      <span className="text-gray-600">-</span>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <ProspectStatusBadge prospect={prospect} />
                  </td>
                  <td className="py-3 pr-4 text-gray-500 text-xs">
                    {prospect.codePostal || '-'}
                  </td>
                  <td className="py-3 text-gray-500 text-xs">
                    {new Date(prospect.createdAt).toLocaleDateString('fr-FR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Badge statut prospect
const ProspectStatusBadge = ({ prospect }) => {
  if (prospect.promoUsed) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
        ✅ Converti
      </span>
    );
  }
  if (prospect.emailConfirmed) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
        📧 Confirmé
      </span>
    );
  }
  return (
    <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
      ⏳ En attente
    </span>
  );
};
